/**
 * AJUSTES DE IMAGEN, resueltos a numeros.
 *
 * Vive en `design-core` porque los tres sitios que dibujan una imagen —el
 * lienzo, el mockup y el compositor de produccion— tienen que aplicar
 * EXACTAMENTE los mismos valores. Si cada uno interpretara «contraste 1,4» a su
 * manera, el cliente aprobaria una cosa y el taller imprimiria otra.
 *
 * La salida es una matriz de color de 20 numeros, que es el formato que
 * entienden tanto el filtro `feColorMatrix` de SVG como el `ColorMatrix` de
 * Fabric. Asi los dos consumen el MISMO calculo en vez de reimplementarlo.
 */
import type { AjustesImagen } from '../schema/layers.js';
import { previewRgb } from '../schema/color.js';

/** Sin ajustes: lo que hay es lo que se ve. */
export const AJUSTES_NEUTROS: AjustesImagen = {
  brillo: 1,
  contraste: 1,
  saturacion: 1,
  modo: 'ninguno',
  umbral: 0.5,
};

/**
 * Coeficientes de luminancia de Rec. 709.
 *
 * No son iguales porque el ojo no ve igual los tres canales: el verde pesa seis
 * veces mas que el azul. Usar un tercio para cada uno da grises sucios, con los
 * rojos demasiado claros y los azules casi negros.
 */
const LUM_R = 0.2126;
const LUM_G = 0.7152;
const LUM_B = 0.0722;

type Matriz = number[];

/** Multiplica dos matrices de color 4x5 (la segunda se aplica DESPUES). */
function componer(a: Matriz, b: Matriz): Matriz {
  const salida: Matriz = new Array(20).fill(0);
  for (let fila = 0; fila < 4; fila += 1) {
    for (let columna = 0; columna < 5; columna += 1) {
      let suma = 0;
      for (let k = 0; k < 4; k += 1) {
        suma += (b[fila * 5 + k] as number) * (a[k * 5 + columna] as number);
      }
      // La quinta columna es el desplazamiento: se le suma el de la segunda.
      if (columna === 4) suma += b[fila * 5 + 4] as number;
      salida[fila * 5 + columna] = suma;
    }
  }
  return salida;
}

const IDENTIDAD: Matriz = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

function matrizBrillo(valor: number): Matriz {
  return [valor, 0, 0, 0, 0, 0, valor, 0, 0, 0, 0, 0, valor, 0, 0, 0, 0, 0, 1, 0];
}

function matrizContraste(valor: number): Matriz {
  // El contraste pivota sobre el gris medio: sin el desplazamiento, subirlo
  // aclararia la imagen entera en vez de separar claros y oscuros.
  const desplazamiento = 0.5 * (1 - valor);
  return [
    valor, 0, 0, 0, desplazamiento,
    0, valor, 0, 0, desplazamiento,
    0, 0, valor, 0, desplazamiento,
    0, 0, 0, 1, 0,
  ];
}

function matrizSaturacion(valor: number): Matriz {
  const r = LUM_R * (1 - valor);
  const g = LUM_G * (1 - valor);
  const b = LUM_B * (1 - valor);
  return [
    r + valor, g, b, 0, 0,
    r, g + valor, b, 0, 0,
    r, g, b + valor, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

/**
 * La matriz de color de unos ajustes.
 *
 * El orden importa: primero brillo, luego contraste y al final saturacion. Es
 * el orden de los editores de foto, y cambiarlo daria resultados distintos con
 * los mismos numeros.
 */
export function matrizDeAjustes(ajustes: AjustesImagen): Matriz {
  let m = IDENTIDAD;
  if (ajustes.brillo !== 1) m = componer(m, matrizBrillo(ajustes.brillo));
  if (ajustes.contraste !== 1) m = componer(m, matrizContraste(ajustes.contraste));

  if (ajustes.modo === 'grises' || ajustes.modo === 'contraste-alto' || ajustes.modo === 'una-tinta') {
    // En estos tres modos el color de partida sobra: se pasa a luminancia.
    m = componer(m, matrizSaturacion(0));
  } else if (ajustes.saturacion !== 1) {
    m = componer(m, matrizSaturacion(ajustes.saturacion));
  }

  return m;
}

/** La matriz en el formato que pide `feColorMatrix` de SVG: veinte numeros. */
export function matrizSvg(ajustes: AjustesImagen): string {
  return matrizDeAjustes(ajustes)
    .map((n) => Number(n.toFixed(6)))
    .join(' ');
}

/**
 * Los modos duros no son una matriz: hay que decidir por pixel.
 *
 * 'contraste-alto' y 'una-tinta' comparan la luminancia con un umbral, y eso no
 * lo hace una matriz lineal. Se declara aparte para que quien dibuje sepa que
 * ademas de la matriz tiene que aplicar un corte.
 */
export function necesitaUmbral(ajustes: AjustesImagen): boolean {
  return ajustes.modo === 'contraste-alto' || ajustes.modo === 'una-tinta';
}

/** Luminancia percibida de un color, de 0 a 1. */
export function luminancia(r: number, g: number, b: number): number {
  return (LUM_R * r + LUM_G * g + LUM_B * b) / 255;
}

/**
 * Aplica los ajustes a un pixel. Es la referencia: si el lienzo y el compositor
 * no coinciden, esta funcion decide quien tiene razon.
 */
export function aplicarAPixel(
  ajustes: AjustesImagen,
  pixel: { r: number; g: number; b: number; a: number },
): { r: number; g: number; b: number; a: number } {
  const m = matrizDeAjustes(ajustes);
  const canal = (fila: number): number => {
    const valor =
      (m[fila * 5] as number) * pixel.r +
      (m[fila * 5 + 1] as number) * pixel.g +
      (m[fila * 5 + 2] as number) * pixel.b +
      (m[fila * 5 + 4] as number) * 255;
    return Math.max(0, Math.min(255, valor));
  };

  let r = canal(0);
  let g = canal(1);
  let b = canal(2);

  if (necesitaUmbral(ajustes)) {
    const claro = luminancia(r, g, b) >= ajustes.umbral;
    if (ajustes.modo === 'contraste-alto') {
      const v = claro ? 255 : 0;
      r = v;
      g = v;
      b = v;
    } else {
      // Una tinta: lo oscuro se pinta con la tinta y lo claro desaparece. Es lo
      // que hace imprimible una foto en serigrafia de un solo color.
      if (claro) return { r: 0, g: 0, b: 0, a: 0 };
      const tinta = ajustes.tinta ? previewRgb(ajustes.tinta) : { r: 0, g: 0, b: 0 };
      return { r: tinta.r, g: tinta.g, b: tinta.b, a: pixel.a };
    }
  }

  return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: pixel.a };
}

/**
 * Pixeles que ocupa un recorte sobre el fichero original.
 *
 * Hace falta para saber la resolucion REAL de lo que queda: recortar un cuarto
 * de una foto deja un cuarto de los pixeles, y el aviso de resolucion tiene que
 * enterarse o dejaria pasar una imagen que ya no cumple.
 */
export function pixelesTrasRecorte(
  intrinsic: { widthPx: number; heightPx: number },
  recorte: { widthFrac: number; heightFrac: number } | undefined,
): { widthPx: number; heightPx: number } {
  if (!recorte) return { widthPx: intrinsic.widthPx, heightPx: intrinsic.heightPx };
  return {
    widthPx: Math.max(1, Math.round(intrinsic.widthPx * recorte.widthFrac)),
    heightPx: Math.max(1, Math.round(intrinsic.heightPx * recorte.heightFrac)),
  };
}

/**
 * Recorte que deja la imagen con una proporcion concreta, centrado.
 *
 * «Cuadrado» o «3 a 4» son las peticiones normales; calcular las fracciones a
 * mano no lo hace nadie.
 */
export function recorteConProporcion(
  intrinsic: { widthPx: number; heightPx: number },
  proporcion: number,
): { xFrac: number; yFrac: number; widthFrac: number; heightFrac: number } {
  const actual = intrinsic.widthPx / intrinsic.heightPx;
  if (proporcion <= 0 || !Number.isFinite(proporcion)) {
    return { xFrac: 0, yFrac: 0, widthFrac: 1, heightFrac: 1 };
  }

  if (actual > proporcion) {
    // Sobra ancho: se recorta por los lados.
    const widthFrac = proporcion / actual;
    return { xFrac: (1 - widthFrac) / 2, yFrac: 0, widthFrac, heightFrac: 1 };
  }
  // Sobra alto: se recorta por arriba y por abajo.
  const heightFrac = actual / proporcion;
  return { xFrac: 0, yFrac: (1 - heightFrac) / 2, widthFrac: 1, heightFrac };
}
